import { useEffect, useState } from "react";
import { Save, Loader2, Settings as SettingsIcon, Image as ImageIcon, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/ImageUpload";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function SystemSettingsPage() {
  const { user } = useAuth();
  const { settings, refresh, update } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  if (!user?.is_superadmin) {
    return (
      <div className="text-center p-12">
        <SettingsIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
        <div className="text-xl font-display font-bold mb-1">غير مصرح</div>
        <p className="text-muted-foreground">هذه الصفحة متاحة لمدير النظام فقط</p>
      </div>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await update(form);
      await refresh();
      toast.success("تم حفظ الإعدادات");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="system-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">الإعدادات العامة</h1>
          <p className="text-muted-foreground mt-1">تخصيص شامل لهوية النظام والوثائق الرسمية</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Save className="w-4 h-4 ms-2" />}
          حفظ الإعدادات
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Identity */}
        <div className="gov-card p-6 lg:col-span-1 lg:row-span-1">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-primary" />
            <div className="font-display text-lg font-semibold">هوية النظام</div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">الشعار</Label>
              <ImageUpload value={form.logo_url} onChange={(v) => setField("logo_url", v)} label="ارفع شعار النظام" testid="settings-logo" height={140} />
            </div>
            <div>
              <Label className="text-sm">اسم النظام</Label>
              <Input data-testid="settings-app-name" value={form.app_name || ""} onChange={(e) => setField("app_name", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">العنوان الفرعي</Label>
              <Input data-testid="settings-app-subtitle" value={form.app_subtitle || ""} onChange={(e) => setField("app_subtitle", e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        {/* PDF defaults */}
        <div className="gov-card p-6 lg:col-span-3">
          <div className="flex items-center gap-2 mb-4">
            <Type className="w-4 h-4 text-primary" />
            <div className="font-display text-lg font-semibold">إعدادات الـ PDF الافتراضية</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">عنوان الترويسة الافتراضي</Label>
              <Input data-testid="settings-pdf-header-title" value={form.pdf_default_header_title || ""} onChange={(e) => setField("pdf_default_header_title", e.target.value)} className="mt-1" />
              <p className="text-xs text-muted-foreground mt-1">يُستخدم عند عدم تحديد ترويسة لكل نموذج</p>
            </div>
            <div>
              <Label className="text-sm">العنوان الفرعي للترويسة</Label>
              <Input data-testid="settings-pdf-header-subtitle" value={form.pdf_default_header_subtitle || ""} onChange={(e) => setField("pdf_default_header_subtitle", e.target.value)} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">نص التذييل الافتراضي</Label>
              <Textarea data-testid="settings-pdf-footer" rows={3} value={form.pdf_footer_text || ""} onChange={(e) => setField("pdf_footer_text", e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
